"use client";

import { useEffect, useState } from "react";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import type { PostHog } from "posthog-js";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [client, setClient] = useState<PostHog | null>(null);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_ANALYTICS !== "true") return;
    // Delay PostHog init until after page is interactive (3s or user interaction)
    const loadPostHog = () => {
      import("posthog-js").then((mod) => {
        const posthog = mod.default;
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
          defaults: "2026-01-30",
          capture_pageview: false,
          capture_pageleave: true,
          loaded: (ph: any) => {
            if (process.env.NODE_ENV === "development") {
              ph.opt_out_capturing();
            }
          },
        });
        setClient(posthog);
      });
    };

    // Load after 3 seconds or on first user interaction, whichever comes first
    const timer = setTimeout(loadPostHog, 3000);
    const events = ["scroll", "click", "touchstart"];
    const onInteraction = () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onInteraction));
      loadPostHog();
    };
    events.forEach((e) => window.addEventListener(e, onInteraction, { once: true }));

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onInteraction));
    };
  }, []);

  // PHProvider is always the wrapper (same element type across renders), so
  // swapping in the real client once it loads doesn't remount `children`.
  return <PHProvider client={client ?? undefined}>{children}</PHProvider>;
}
